import clsx from 'clsx';
import HeartIcon from 'components/assets/dollar-heart.svg';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import Copyable from 'components/atoms/Copyable/Copyable';
import Currency from 'components/atoms/Currency/Currency';
import Txt from 'components/atoms/Txt/Txt';
import usePanelConfiguration from 'hooks/usePanelConfiguration';
import React from 'react';
import { graphql, useFragment } from 'react-relay';
import arrayOf from 'utils/arrayOf';
import { ReferAFriend_referralsRef$key } from './__generated__/ReferAFriend_referralsRef.graphql';
import PopupIcon from './assets/popup.svg';
import classes from './ReferAFriend.module.scss';

export type Props = {
  referralsRef: ReferAFriend_referralsRef$key;
  className?: string;
  onOpenMetrics?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

const ReferAFriendFragment = graphql`
  fragment ReferAFriend_referralsRef on Query {
    referralHistory {
      currentMonth {
        successes
      }
    }
    referralLink
  }
`;

export default function ReferAFriend({ referralsRef, className, onOpenMetrics }: Props) {
  const data = useFragment(ReferAFriendFragment, referralsRef);
  const panelConfig = usePanelConfiguration();
  const shortLink = data.referralLink;
  const monthlyReferralSuccessProgress = data.referralHistory.currentMonth?.successes || 0;
  const rewardAmount = (panelConfig?.referralsOptions.referrerAmountCents ?? 50) / 100;
  const rewardCurrency = panelConfig?.referralsOptions.panelistAmountCurrency;

  const shortenUrl = (url: string) => {
    const parts = new URL(url);
    return `${parts.hostname}${parts.pathname}`;
  };

  return (
    shortLink &&
    panelConfig?.referralsOptions.enabled &&
    rewardAmount &&
    rewardCurrency && (
      <Card className={clsx(classes.root, className)}>
        <div className={classes.container}>
          <ol className={classes.tutorialList}>
            <li>
              <Txt>
                Copy & paste your unique referral code into a text, email or social media post.
              </Txt>
            </li>
            <li>
              <Txt>
                Your friends need to click the link to sign up & successfully complete their first 3
                surveys.
              </Txt>
            </li>
            <li>
              <Txt>
                Voila! You receive an instant{' '}
                <Currency
                  amount={rewardAmount}
                  currency={rewardCurrency}
                  options={{
                    minimumFractionDigits: Number.isInteger(rewardAmount) ? 0 : 2,
                    maximumFractionDigits: 2,
                  }}
                />{' '}
                reward.
              </Txt>
            </li>
          </ol>
          <Copyable
            clipboardValue={shortLink}
            displayValue={shortenUrl(shortLink)}
            title="Your unique referral code"
            className={classes.shortLink}
          />
          <div className={classes.progressContainer}>
            <Txt component="p">
              Complete <b>5 successful referrals</b> each month
            </Txt>
            <div className={classes.progressIcons}>
              {arrayOf(5, (index) => (
                <HeartIcon
                  key={index}
                  className={clsx(
                    classes.heartIcon,
                    index < monthlyReferralSuccessProgress && classes.heartIconActive,
                  )}
                />
              ))}
            </div>
          </div>
          <hr className={classes.divider} />
          <Button
            buttonType="text"
            component="a"
            className={classes.referralLink}
            onClick={onOpenMetrics ?? undefined}
            onAuxClick={onOpenMetrics}
          >
            <Txt>Referral Tracking</Txt>
            <PopupIcon />
          </Button>
        </div>
      </Card>
    )
  );
}
